import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Layers, 
  DoorOpen, 
  Users, 
  TrendingUp,
  ClipboardList,
  ArrowRight
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import StatCard from '../../components/ui/StatCard';
import { adminsAPI, stationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, logsRes] = await Promise.all([
        adminsAPI.getDashboard(),
        adminsAPI.getActivityLogs({ limit: 5 })
      ]);
      
      setStats(dashboardRes.data.stats);
      setRecentActivity(logsRes.data.logs);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickLinks = [
    { to: '/superadmin/stations', icon: Building2, label: 'Manage Stations', color: 'bg-blue-500' },
    { to: '/superadmin/categories', icon: Layers, label: 'Room Categories', color: 'bg-purple-500' },
    { to: '/superadmin/rooms', icon: DoorOpen, label: 'All Rooms', color: 'bg-green-500' },
    { to: '/superadmin/admins', icon: Users, label: 'Admin Accounts', color: 'bg-orange-500' },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-midnight-900">
            Super Admin Dashboard
          </h1>
          <p className="text-midnight-600 mt-1">
            Overview of the Retiring Room System
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Total Stations" 
            value={stats?.totalStations || 0}
            icon={Building2}
            color="blue"
          />
          <StatCard 
            title="Total Rooms" 
            value={stats?.totalRooms || 0}
            icon={DoorOpen}
            color="green"
          />
          <StatCard 
            title="Active Admins" 
            value={stats?.totalAdmins || 0}
            icon={Users}
            color="purple"
          />
          <StatCard 
            title="Pending Requests" 
            value={stats?.pendingRequests || 0}
            icon={ClipboardList}
            color="orange"
          />
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-display font-semibold text-midnight-900 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="card p-6 hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${link.color} rounded-xl flex items-center justify-center`}>
                      <link.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-semibold text-midnight-900">{link.label}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-midnight-300 group-hover:text-railway-500 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-display font-semibold">Recent Activity</h2>
            <TrendingUp className="w-5 h-5 text-white/60" />
          </div>
          <div className="p-6">
            {recentActivity.length === 0 ? (
              <p className="text-midnight-500 text-center py-8">No recent activity</p>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((log) => (
                  <div 
                    key={log._id}
                    className="flex items-start gap-4 p-4 bg-midnight-50 rounded-xl"
                  >
                    <div className="w-10 h-10 bg-midnight-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-midnight-600">
                        {log.admin?.name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-midnight-900 font-medium">
                        {log.admin?.name || 'Unknown Admin'}
                      </p>
                      <p className="text-midnight-600 text-sm">
                        {log.action.replace(/_/g, ' ')}
                        {log.details?.name && ` - ${log.details.name}`}
                      </p>
                      <p className="text-midnight-400 text-xs mt-1">
                        {new Date(log.createdAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SuperAdminDashboard;
